/**
 * Phase 27.37.E — Unmatched / Ambiguous resolution UI.
 *
 * Rendered by SmsInboxPage in the right pane when the selected
 * conversation has `contact_type === 'unknown' | 'ambiguous'`.
 *
 * For an *ambiguous* conversation the backend already stored the
 * list of candidates on the conversation — we render them so a KR
 * user can pick the right record in one click.
 *
 * For an *unmatched* conversation the operator can search Leads /
 * Prospects / Agents and link to any authorized record.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, HelpCircle, Search, Trash2, Send, Loader2, MessageSquare } from 'lucide-react';
import { smsInboxAPI } from '@/lib/api';
import api from '@/lib/api';
import { getErrorMessage, formatPhone } from '@/lib/utils';

export default function UnmatchedResolutionPanel({ conversation, onResolved }) {
  const isAmbiguous = conversation.contact_type === 'ambiguous';
  const candidates = conversation.ambiguous_candidates || [];
  const [linking, setLinking] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [searchType, setSearchType] = useState('lead');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const loadThread = useCallback(async () => {
    if (!conversation?.conversation_id) return;
    setLoadingThread(true);
    try {
      const r = await smsInboxAPI.messages(conversation.conversation_id);
      setMessages(r.data?.messages || []);
    } catch (e) {
      // A missing thread is not fatal — the link/dismiss tools still work.
      setMessages([]);
    } finally { setLoadingThread(false); }
  }, [conversation?.conversation_id]);

  useEffect(() => { loadThread(); }, [loadThread]);

  const sendReply = async () => {
    const body = replyBody.trim();
    if (!body) return;
    setSending(true);
    try {
      await smsInboxAPI.reply(conversation.conversation_id, { body });
      setReplyBody('');
      await loadThread();
      toast.success('Reply sent');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send reply'));
    } finally { setSending(false); }
  };

  const link = async (contact_type, contact_id) => {
    setLinking(true);
    try {
      await smsInboxAPI.link(conversation.conversation_id, { contact_type, contact_id });
      toast.success('Conversation linked');
      onResolved?.();
    } catch (e) { toast.error(getErrorMessage(e, 'Link failed')); }
    finally { setLinking(false); }
  };

  const dismiss = async () => {
    if (!window.confirm('Dismiss this unmatched conversation? It will be archived but not deleted.')) return;
    setDismissing(true);
    try {
      await smsInboxAPI.dismiss(conversation.conversation_id);
      toast.success('Conversation dismissed');
      onResolved?.();
    } catch (e) { toast.error(getErrorMessage(e, 'Dismiss failed')); }
    finally { setDismissing(false); }
  };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      if (searchType === 'lead') {
        const r = await api.get('/leads', { params: { q, limit: 10 } });
        setResults((r.data?.leads || []).map((l) => ({
          contact_type: 'lead', contact_id: l.lead_id,
          display: `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email || l.lead_id,
          phone: l.phone,
        })));
      } else if (searchType === 'prospect') {
        const r = await api.get('/prospects', { params: { search: q, limit: 10 } });
        setResults((r.data?.items || []).map((p) => ({
          contact_type: 'prospect', contact_id: p.prospect_id,
          display: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || p.prospect_id,
          phone: p.phone,
        })));
      } else if (searchType === 'agent_user') {
        const r = await api.get('/users', { params: { q, limit: 10 } });
        const users = r.data?.users || r.data?.items || r.data || [];
        setResults((Array.isArray(users) ? users : []).map((u) => ({
          contact_type: 'agent_user', contact_id: u.user_id,
          display: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.user_id,
          phone: u.phone || u.mobile,
        })));
      }
    } catch (e) { toast.error(getErrorMessage(e, 'Search failed')); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-4" data-testid="unmatched-resolution-panel">
      <div className="flex items-start gap-2 p-3 rounded border border-amber-200 bg-amber-50">
        {isAmbiguous ? <AlertTriangle size={16} className="text-amber-600 mt-0.5" /> : <HelpCircle size={16} className="text-amber-600 mt-0.5" />}
        <div className="text-sm text-slate-700">
          <p className="font-medium mb-0.5">
            {isAmbiguous ? 'This phone number matches more than one record' : 'This phone number is not linked to any known contact'}
          </p>
          <p className="text-xs text-slate-600">
            {isAmbiguous
              ? 'Pick the correct record so future messages from this number thread automatically.'
              : 'Search below to link this conversation to a Lead, Prospect, or Agent — or dismiss if it is spam.'}
          </p>
          <p className="mt-1 text-xs text-slate-500 font-mono">Sender: {conversation.contact_phone}</p>
        </div>
      </div>

      {/* Message thread — always shown so the operator can read the
          actual SMS text and reply, even before linking a contact. */}
      <div data-testid="unmatched-thread">
        <p className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
          <MessageSquare size={12} /> Conversation
        </p>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-72 overflow-y-auto space-y-2">
          {loadingThread ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-4 justify-center">
              <Loader2 size={14} className="animate-spin" /> Loading messages…
            </div>
          ) : messages.length === 0 ? (
            <p className="text-xs italic text-slate-400 text-center py-4">No messages yet.</p>
          ) : (
            messages.map((m) => {
              const outbound = (m.direction || '').startsWith('outbound');
              return (
                <div key={m.message_id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${outbound ? 'bg-sky-600 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <p className={`text-[10px] mt-0.5 ${outbound ? 'text-sky-100' : 'text-slate-400'}`}>
                      {m.created_at ? new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                      {m.status ? ` · ${m.status}` : ''}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
            placeholder="Type a reply…"
            className="h-9 text-sm"
            data-testid="unmatched-reply-input"
          />
          <Button size="sm" onClick={sendReply} disabled={sending || !replyBody.trim()} data-testid="unmatched-reply-send">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </Button>
        </div>
      </div>

      {isAmbiguous && candidates.length > 0 && (
        <div className="space-y-2" data-testid="ambiguous-candidates">
          <p className="text-xs font-medium text-slate-600">Candidates</p>
          {candidates.map((c) => (
            <div key={`${c.contact_type}:${c.contact_id}`} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-800">{c.display_name}</div>
                <div className="text-xs text-slate-500">
                  <Badge variant="outline" className="mr-2 text-[10px]">{c.contact_type}</Badge>
                  <span className="font-mono">{formatPhone(c.phone)}</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => link(c.contact_type, c.contact_id)}
                disabled={linking}
                data-testid={`link-candidate-${c.contact_id}`}
              >Link</Button>
            </div>
          ))}
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-slate-600 mb-1.5">
          {isAmbiguous ? 'Or search for a different contact' : 'Search a contact to link'}
        </p>
        <div className="flex items-center gap-2">
          <select
            value={searchType}
            onChange={(e) => { setSearchType(e.target.value); setResults([]); }}
            className="text-xs border border-slate-200 rounded px-2 py-1.5 h-8 bg-white"
            data-testid="search-type-select"
          >
            <option value="lead">Lead</option>
            <option value="prospect">Prospect</option>
            <option value="agent_user">Agent</option>
          </select>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="Name, phone, or email"
            className="h-8 text-sm"
            data-testid="search-input"
          />
          <Button size="sm" onClick={search} disabled={searching} data-testid="search-btn">
            <Search size={12} className="mr-1" /> Search
          </Button>
        </div>
        {results.length > 0 && (
          <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
            {results.map((r) => (
              <li key={`${r.contact_type}:${r.contact_id}`} className="flex items-center justify-between px-3 py-2">
                <div>
                  <div className="text-sm text-slate-800">{r.display}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{formatPhone(r.phone) || 'no phone on file'}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => link(r.contact_type, r.contact_id)} disabled={linking}>
                  Link
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pt-2 border-t border-slate-100 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={dismiss}
          disabled={dismissing}
          className="text-red-600 hover:text-red-700"
          data-testid="inbox-dismiss-btn"
        >
          <Trash2 size={13} className="mr-1" /> Dismiss
        </Button>
      </div>
    </div>
  );
}
